/**
 * FP3-042 — Account Security page.
 *
 * Authenticated surface where any logged-in user can:
 *   - update their first name / last name (no password required)
 *   - update their email (current password required — Option C)
 *   - change their password (current + new + confirm, new ≥ 12)
 *
 * Role / permission / org / status are never editable here.
 *
 * Mounted at /account/security inside the authenticated AppLayout
 * shell so the sidebar stays visible.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, User, EnvelopeSimple, Lock } from "@phosphor-icons/react";

import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const PASSWORD_MIN = 12;

export default function AccountSecurity() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  // Profile form local state — seeded synchronously from `user`
  // (the auth context is already populated by the ProtectedRoute
  // wrapper, so this avoids a flash of empty inputs on hard refresh)
  // and then refined from /account/me which is the server's source
  // of truth.
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [seedEmail, setSeedEmail] = useState(user?.email || "");
  const [profileCurrentPw, setProfileCurrentPw] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");

  // Password form local state.
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      try {
        const r = await api.getAccountMe();
        if (cancelled) return;
        const me = r.data || {};
        setFirstName(me.first_name || "");
        setLastName(me.last_name || "");
        setEmail(me.email || "");
        setSeedEmail(me.email || "");
      } catch {
        // Fall back to whatever the auth context already has.
        setFirstName(user?.first_name || "");
        setLastName(user?.last_name || "");
        setEmail(user?.email || "");
        setSeedEmail(user?.email || "");
      }
    }
    hydrate();
    return () => {
      cancelled = true;
    };
    // We intentionally re-run only on mount + when the auth user id
    // changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const emailChanging = useMemo(
    () =>
      (email || "").trim().toLowerCase() !==
      (seedEmail || "").trim().toLowerCase(),
    [email, seedEmail]
  );

  const nameChanging = useMemo(
    () =>
      (firstName || "").trim() !== (user?.first_name || "") ||
      (lastName || "").trim() !== (user?.last_name || ""),
    [firstName, lastName, user?.first_name, user?.last_name]
  );

  const profileDirty = nameChanging || emailChanging;

  const onSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError("");
    if (!profileDirty) {
      toast.info("No changes to save");
      return;
    }
    if (emailChanging) {
      if (!profileCurrentPw) {
        setProfileError("Current password is required to change your email.");
        return;
      }
    }

    const payload = {};
    if ((firstName || "").trim() !== (user?.first_name || "")) {
      payload.first_name = (firstName || "").trim();
    }
    if ((lastName || "").trim() !== (user?.last_name || "")) {
      payload.last_name = (lastName || "").trim();
    }
    if (emailChanging) {
      payload.email = (email || "").trim().toLowerCase();
      payload.current_password = profileCurrentPw;
    }

    setProfileSaving(true);
    try {
      const r = await api.updateAccountProfile(payload);
      const fresh = r.data || {};
      // Resync local form state from server response.
      setFirstName(fresh.first_name || "");
      setLastName(fresh.last_name || "");
      setEmail(fresh.email || "");
      setSeedEmail(fresh.email || "");
      setProfileCurrentPw("");
      toast.success(emailChanging ? "Email and profile updated" : "Profile updated");
      if (refresh) {
        try {
          await refresh();
        } catch {
          /* noop — fresh /account/me already in state */
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setProfileError(detail || "Could not update profile. Please try again.");
    } finally {
      setProfileSaving(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (!pwCurrent) {
      setPwError("Current password is required.");
      return;
    }
    if (pwNew.length < PASSWORD_MIN) {
      setPwError(`New password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    if (pwNew === pwCurrent) {
      setPwError("New password must be different from your current password.");
      return;
    }

    setPwSaving(true);
    try {
      await api.changeAccountPassword({
        current_password: pwCurrent,
        new_password: pwNew,
        confirm_new_password: pwConfirm,
      });
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      toast.success("Password updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPwError(detail || "Could not change password. Please try again.");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div
      data-testid="account-security-page"
      className="min-h-full bg-[#F7F5EF] body-font px-5 py-8 md:px-10 md:py-10"
    >
      <div className="max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm">
            <ShieldCheck size={20} className="text-white" weight="duotone" />
          </div>
          <div>
            <h1
              data-testid="account-security-heading"
              className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight"
            >
              Account Security
            </h1>
            <p className="text-sm text-[#6B7280] mt-0.5">
              Update your profile and password. Role and organization
              are managed by your administrator.
            </p>
          </div>
        </div>

        {/* Profile card */}
        <Card
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl mb-6"
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-[#1F2937] heading-font">
              <User size={18} className="text-[#1E3F33]" weight="duotone" />
              Profile
            </CardTitle>
          </CardHeader>
          <Separator className="bg-[#E5E3DB]" />
          <CardContent className="pt-5">
            <form
              onSubmit={onSaveProfile}
              className="space-y-5"
              data-testid="account-profile-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="account-first-name" className="text-xs uppercase tracking-wide text-[#6B7280]">
                    First name
                  </Label>
                  <Input
                    id="account-first-name"
                    data-testid="account-first-name-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    className="bg-white border-[#E5E3DB]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="account-last-name" className="text-xs uppercase tracking-wide text-[#6B7280]">
                    Last name
                  </Label>
                  <Input
                    id="account-last-name"
                    data-testid="account-last-name-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    className="bg-white border-[#E5E3DB]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="account-email" className="text-xs uppercase tracking-wide text-[#6B7280] flex items-center gap-1.5">
                  <EnvelopeSimple size={12} weight="duotone" />
                  Email
                </Label>
                <Input
                  id="account-email"
                  data-testid="account-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="bg-white border-[#E5E3DB]"
                />
                {emailChanging && (
                  <p
                    data-testid="account-email-warning"
                    className="text-xs text-[#7A6A2C] bg-[#FDF3D8] border border-[#F1E2A8] rounded-md px-3 py-2 mt-1.5"
                  >
                    Changing your email requires your current password
                    below. We&apos;ll send future password-reset links
                    to the new address.
                  </p>
                )}
              </div>

              {emailChanging && (
                <div className="space-y-1.5">
                  <Label htmlFor="account-profile-current-pw" className="text-xs uppercase tracking-wide text-[#6B7280]">
                    Current password
                  </Label>
                <PasswordInput
                  id="account-profile-current-pw"
                  data-testid="account-profile-current-password-input"
                  value={profileCurrentPw}
                  onChange={(e) => setProfileCurrentPw(e.target.value)}
                  autoComplete="current-password"
                  className="bg-white border-[#E5E3DB]"
                />
                </div>
              )}

              {profileError && (
                <p
                  data-testid="account-profile-error"
                  className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2"
                >
                  {profileError}
                </p>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="account-profile-cancel-btn"
                  onClick={() => navigate(-1)}
                  className="text-[#6B7280]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="account-profile-save-btn"
                  disabled={profileSaving || !profileDirty}
                  className="bg-[#1E3F33] hover:bg-[#163029] text-white"
                >
                  {profileSaving ? "Saving…" : "Save profile"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Password card */}
        <Card
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-[#1F2937] heading-font">
              <Lock size={18} className="text-[#1E3F33]" weight="duotone" />
              Change password
            </CardTitle>
          </CardHeader>
          <Separator className="bg-[#E5E3DB]" />
          <CardContent className="pt-5">
            <form
              onSubmit={onChangePassword}
              className="space-y-5"
              data-testid="account-password-form"
            >
              <div className="space-y-1.5">
                <Label htmlFor="account-pw-current" className="text-xs uppercase tracking-wide text-[#6B7280]">
                  Current password
                </Label>
                <PasswordInput
                  id="account-pw-current"
                  data-testid="account-password-current-input"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  autoComplete="current-password"
                  className="bg-white border-[#E5E3DB]"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="account-pw-new" className="text-xs uppercase tracking-wide text-[#6B7280]">
                  New password
                </Label>
                <PasswordInput
                  id="account-pw-new"
                  data-testid="account-password-new-input"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  autoComplete="new-password"
                  className="bg-white border-[#E5E3DB]"
                />
                <p className="text-[11px] text-[#6B7280]">
                  Minimum {PASSWORD_MIN} characters. Use a passphrase
                  you don&apos;t use anywhere else.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="account-pw-confirm" className="text-xs uppercase tracking-wide text-[#6B7280]">
                  Confirm new password
                </Label>
                <PasswordInput
                  id="account-pw-confirm"
                  data-testid="account-password-confirm-input"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="bg-white border-[#E5E3DB]"
                />
              </div>

              {pwError && (
                <p
                  data-testid="account-password-error"
                  className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2"
                >
                  {pwError}
                </p>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="submit"
                  data-testid="account-password-save-btn"
                  disabled={
                    pwSaving ||
                    !pwCurrent ||
                    !pwNew ||
                    !pwConfirm ||
                    pwNew.length < PASSWORD_MIN ||
                    pwNew !== pwConfirm
                  }
                  className="bg-[#1E3F33] hover:bg-[#163029] text-white"
                >
                  {pwSaving ? "Updating…" : "Update password"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
